import React, { useLayoutEffect, useRef } from 'react';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { FileUpload } from 'primereact/fileupload';
import { Image as PrimeImage } from 'primereact/image';
import { Field } from 'formik';
import { Button } from 'primereact/button';
import { SHOP_TYPES } from '../../../screens/private/FirstTimeSetup/constant';
import { logError } from '../../../utils/analytics';
import './styles.css';

export default function BasicInformationForm({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  setFieldError,
  nameCheckStatus,
  onNameChange,
  isEditMode = false
}) {
  const toast = useRef(null);
  const [previews, setPreviews] = React.useState([]);

  const handleNameChangeWrapper = (e, form) => {
    handleChange(e);
    onNameChange(e, form.setFieldValue, form.setFieldError);
  };

  // Sync previews with storeImages after deletions to prevent stale blobs from being displayed
  useLayoutEffect(() => {
    setPreviews(prev => prev.filter(p => (values.storeImages || []).some(img => typeof img === 'object' && img.id === p.id)));
  }, [values.storeImages]);

  const groupedItemTemplate = (option) => (
    <div className="flex align-items-center">
      <div className="category-label">{option.label}</div>
    </div>
  );

  const categoryLabel = (option) => (
    <div className="category-label">{option.label}</div>
  );

  const onUpload = (e) => {
    const files = Array.from(e.files);
    const currentImages = values.storeImages || [];
    const remainingSlots = 5 - currentImages.length;

    if (files.length > remainingSlots) {
      toast.current.show({ severity: 'warn', summary: 'Limit Reached', detail: `You can only upload ${remainingSlots} more images` });
      return;
    }

    const newPreviews = files.slice(0, remainingSlots).map(file => ({
      id: Date.now() + Math.random(),
      url: URL.createObjectURL(file),
      objectURL: URL.createObjectURL(file),
      file
    }));

    setPreviews(prev => [...prev, ...newPreviews]);
    setFieldValue('storeImages', [...currentImages, ...newPreviews]);
  };

  const removeImage = (id) => {
    try {
      const previewIndex = previews.findIndex(p => p.id === id);
      if (previewIndex >= 0) {
        const removedPreview = previews[previewIndex];
        URL.revokeObjectURL(removedPreview.url);
        const updatedPreviews = previews.filter(p => p.id !== id);
        setPreviews(updatedPreviews);
        const updatedImages = (values.storeImages || []).filter(img => {
          if (typeof img === 'object' && img.id !== undefined) {
            return img.id !== id;
          }
          return true;
        });
        setFieldValue('storeImages', updatedImages);
        return;
      }

      const savedIndex = values.storeImages.findIndex(
        (img, index) => typeof img === 'string' && index === (id - previews.length)
      );

      if (savedIndex >= 0) {
        const updatedImages = values.storeImages.filter(
          (_, index) => index !== savedIndex
        );
        setFieldValue('storeImages', updatedImages);
      }
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete image',
        life: 3000
      });
    }
  };

  // Function to be called during form submission to delete Firebase images
  const deleteFirebaseImages = async (imageUrls) => {
    try {
      const storage = getStorage();
      for (const imageUrl of imageUrls) {
        const existingImagePath = decodeURIComponent(imageUrl.split('/o/')[1].split('?')[0]);
        await deleteObject(ref(storage, existingImagePath));
      }
    } catch (error) {
      console.error('Error deleting Firebase images:', error);
      // Don't throw error here as we want to allow form submission even if deletion fails
    }
  };

  return (
    <div className="form-section">
      <h3 className="section-title">Business Information</h3>

      <Field name="storeImages">
        {({ field }) => (
          <div className="field">
            <label>Storefront Images ({values.storeImages.length == 0 ? "Optional" : (values.storeImages || []).length + "/5"})</label>
            <FileUpload
              mode="basic"
              name={field.name}
              accept="image/*"
              auto
              maxFileSize={1000000}
              multiple
              onSelect={onUpload}
              chooseLabel="Upload Images"
              className="w-full"
              disabled={(values.storeImages || []).length >= 5}
            />

            <div className="flex flex-wrap gap-3 mt-3">
              {values.storeImages?.map((img, index) => {
                // Preview object
                if (typeof img === 'object' && img.id !== undefined) {
                  return (
                    <div key={img.id} className="relative w-full md:w-12rem" style={{ height: '12rem' }}>
                      <div className="w-full h-full flex align-items-center justify-content-center bg-gray-100 border-round shadow-2 overflow-hidden" style={{ aspectRatio: '1/1' }}>
                        <div className="w-full h-full flex align-items-center justify-content-center">
                          <PrimeImage
                            src={img.url}
                            alt="Storefront preview"
                            className="max-w-full max-h-full"
                            preview
                            imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={(e) => {
                              e.target.src = '/placeholder-image.png'; 
                            }}
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger p-button-sm absolute"
                        style={{ top: '-10px', right: '-10px' }}
                        onClick={() => removeImage(img.id)}
                      />
                    </div>
                  );
                }
                // Saved string URL
                if (typeof img === 'string') {
                  return (
                    <div key={`saved-${index}`} className="relative w-full md:w-12rem" style={{ height: '12rem' }}>
                      <div className="w-full h-full flex align-items-center justify-content-center bg-gray-100 border-round shadow-2 overflow-hidden" style={{ aspectRatio: '1/1' }}>
                        <div className="w-full h-full flex align-items-center justify-content-center">
                          <PrimeImage
                            src={img}
                            alt="Saved store image"
                            className="max-w-full max-h-full"
                            preview
                            imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger p-button-sm absolute"
                        style={{ top: '-10px', right: '-10px' }}
                        onClick={() => removeImage(index + previews.length)}
                      />
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}
      </Field>

      <Field name="shopName" validate={value => {
        if (!value) return 'Business name is required';
        if (nameCheckStatus.available === false) return nameCheckStatus.message;
        return undefined;
      }}>
        {({ field, meta, form }) => (
          <div className="field">
            <label htmlFor="shopName">Business Name</label>
            <InputText
              id="shopName"
              {...field}
              disabled={isEditMode}
              onChange={(e) => handleNameChangeWrapper(e, form)}
              className={`${meta.touched && meta.error ? 'p-invalid' : ''} w-full`}
            />
            <div className="flex align-items-center mt-1">
              {nameCheckStatus.loading && (
                <div className="name-availability name-checking">
                  <i className="pi pi-spin pi-spinner"></i>
                  <small>Checking availability...</small>
                </div>
              )}
              {!nameCheckStatus.loading && nameCheckStatus?.available !== null && (
                <div className={`name-availability ${nameCheckStatus.available ? 'name-available' : 'name-unavailable'}`}>
                  <i className={`pi ${nameCheckStatus.available ? 'pi-check-circle' : 'pi-times-circle'}`}></i>
                  <small>{nameCheckStatus.message}</small>
                </div>
              )}
            </div>
          </div>
        )}
      </Field>

      <Field name="shopType">
        {({ field, meta }) => (
          <div className="field">
            <label htmlFor="shopType">Business Category (Optional)</label>
            <MultiSelect
              id="shopType"
              value={values.shopType || []}
              filter
              options={SHOP_TYPES}
              onChange={(e) => setFieldValue('shopType', e.value)}
              optionLabel="label"
              optionGroupLabel="label"
              optionGroupChildren="items"
              optionGroupTemplate={groupedItemTemplate}
              itemTemplate={categoryLabel}
              className={`w-full ${meta.touched && meta.error ? 'p-invalid' : ''}`}
              placeholder="Select categories"
              display="chip"
              maxSelectedLabels={3}
              selectedItemsLabel="{0} categories selected"
            />
            {meta.touched && meta.error ? (
              <small className="p-error">{meta.error}</small>
            ) : null}
          </div>
        )}
      </Field>


    </div>
  );
}
